import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "preview", "previewGrid", "grid", "addPlaceholder"]
  static values = {
    limit: { type: Number, default: 9 }
  }

  connect() {
    this.accumulatedFiles = []
    this.renderedCount = 0
  }

  preview() {
    const newFiles = Array.from(this.inputTarget.files)
    if (newFiles.length === 0) return

    // Accumulate new files
    this.accumulatedFiles.push(...newFiles)

    // Rebuild the file input with all accumulated files
    this.syncFileInput()

    // Render previews
    this.renderPreviews()
  }

  renderPreviews() {
    if (!this.hasGridTarget) return

    // Hide the old preview area
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }

    // Get the add placeholder (the label with file input)
    const addPlaceholder = this.hasAddPlaceholderTarget ? this.addPlaceholderTarget : null

    // Count existing photos (server-rendered, have data-blob-id)
    const existingPhotos = this.gridTarget.querySelectorAll('[data-blob-id]').length

    // Render new files as previews
    this.accumulatedFiles.forEach((file, fileIndex) => {
      // Skip already rendered files
      if (fileIndex < this.renderedCount) return

      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) return

      // Check limit
      if (existingPhotos + fileIndex >= this.limitValue) return

      if (file.type.startsWith("image/")) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const preview = this.createImagePreview(e.target.result, fileIndex)
          if (addPlaceholder) {
            this.gridTarget.insertBefore(preview, addPlaceholder)
          } else {
            this.gridTarget.appendChild(preview)
          }
          this.updateAddPlaceholderVisibility()
        }
        reader.readAsDataURL(file)
      } else if (file.type.startsWith("video/")) {
        const preview = this.createVideoPreview(file, fileIndex)
        if (addPlaceholder) {
          this.gridTarget.insertBefore(preview, addPlaceholder)
        } else {
          this.gridTarget.appendChild(preview)
        }
        this.updateAddPlaceholderVisibility()
      }
    })

    this.renderedCount = this.accumulatedFiles.length
  }

  updateAddPlaceholderVisibility() {
    if (!this.hasAddPlaceholderTarget) return

    // Count total photos (existing + new previews)
    const existingPhotos = this.gridTarget.querySelectorAll('[data-blob-id]').length
    const newPreviews = this.gridTarget.querySelectorAll('[data-preview-index]').length
    const total = existingPhotos + newPreviews

    if (total >= this.limitValue) {
      this.addPlaceholderTarget.classList.add('hidden')
    } else {
      this.addPlaceholderTarget.classList.remove('hidden')
    }
  }

  createImagePreview(src, fileIndex) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative group aspect-square"
    wrapper.dataset.previewIndex = fileIndex

    const img = document.createElement("img")
    img.src = src
    img.className = "w-full h-full object-cover rounded-md ring-2 ring-teal-500"

    const removeBtn = this.createRemoveButton(fileIndex)

    wrapper.appendChild(img)
    wrapper.appendChild(removeBtn)
    return wrapper
  }

  createVideoPreview(file, fileIndex) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative group aspect-square bg-slate-100 dark:bg-slate-700 rounded-md flex flex-col items-center justify-center p-2 ring-2 ring-teal-500"
    wrapper.dataset.previewIndex = fileIndex

    wrapper.innerHTML = `
      <svg class="w-8 h-8 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        <path stroke-linecap="round" stroke-linejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z"/>
      </svg>
      <span class="text-xs text-teal-600 dark:text-teal-400 truncate w-full text-center mt-1">${file.name.substring(0, 12)}</span>
    `

    const removeBtn = this.createRemoveButton(fileIndex)
    wrapper.appendChild(removeBtn)
    return wrapper
  }

  createRemoveButton(fileIndex) {
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "absolute top-1 right-1 inline-flex items-center p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
    removeBtn.innerHTML = `
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
        <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `
    removeBtn.addEventListener("click", (e) => {
      e.preventDefault()
      this.removeFile(fileIndex)
    })
    return removeBtn
  }

  removeFile(index) {
    this.accumulatedFiles.splice(index, 1)
    this.syncFileInput()

    // Remove all preview elements and re-render
    if (this.hasGridTarget) {
      const previews = this.gridTarget.querySelectorAll('[data-preview-index]')
      previews.forEach(preview => preview.remove())
    }

    // Reset rendered count and re-render
    this.renderedCount = 0
    this.renderPreviews()
  }

  syncFileInput() {
    const dt = new DataTransfer()
    this.accumulatedFiles.forEach((file) => dt.items.add(file))
    this.inputTarget.files = dt.files
  }
}
